import * as React from 'react'
import { compact, some, keys, isEmpty, includes, find, isNaN } from 'lodash'
import Cell from '../components/cell/cell.grid'
import {
  ColumnProps,
  ColumnInternalType,
  ColumnExternalType,
  ColumnExtraType,
  ExtColumn,
  ExtCellInfo,
  ActionOption
} from '../models/column.props.configuration'
import IconGraph from '../components/type.icon'
import Severity from '../components/severity.indicator'
import CellActions from '../components/cell/cell.actions'
import uuidv4 from 'uuid/v4'
import { formatDateTime } from '@src/utils/date.helpers'
import cx from 'classnames'
import styles from '../data.grid.styl'
import Tooltip from '@mui/material/Tooltip'
import type { ResourceTypeDict } from '@src/redux/reducers/shell'
import { CSSSelector } from '@src/utils/css.selector.helper'
import type { GridAction } from '../models/grid.props.configurations'

export const columnTypeSelector = new CSSSelector('type-')
export const columnFieldSelector = new CSSSelector('field-')

const getColumnTypeSelector = (
  type: ColumnInternalType | ColumnExternalType | ColumnExtraType
) => `${columnTypeSelector.value}${type}`
const getColumnFieldSelector = (field: string) =>
  field ? `${columnFieldSelector.value}${field}` : null

interface ColumnMapper<T> {
  columns: ColumnProps<T>[]
  resolvedState: () => any
  resourceTypes: ResourceTypeDict
  hideEmptyColumns: boolean
  data?: T[]
  keyField?: keyof T
  onExpandRow?: (viewIndex: number, field: keyof T) => void
  actions?: any[]
}

export function columnMapper<T>(props: ColumnMapper<T>) {
  const {
    columns,
    resolvedState,
    resourceTypes,
    hideEmptyColumns,
    data,
    keyField,
    onExpandRow,
    actions = []
  } = { ...props }
  let actionColumn: ColumnProps<T> = find(
    columns,
    x => x.type === ColumnInternalType.actions
  )
  const hasExpander = hasExpanderColumn(columns)
  // append actions column in case we have some
  const rowActions = (actions || []).filter(action => !!action.handler)
  const hasActionColumn = columns.length && actionColumn
  if (!isEmpty(rowActions) && !hasActionColumn) {
    actionColumn = {
      type: ColumnInternalType.actions,
      width: 120,
      actionOptions: rowActions
    }
  }
  if (!isEmpty(rowActions) && hasActionColumn) {
    actionColumn = { ...actionColumn }
    actionColumn.actionOptions = rowActions
  }
  const parsedColumns: ExtColumn<T>[] = compact<ExtColumn<T>>(
    compact([...columns, actionColumn]).map<ExtColumn<T>>(
      (col, index) =>
        factoryColumnByType({
          columns,
          resolvedState,
          column: col,
          index,
          resourceTypes,
          hideEmptyColumns,
          data,
          keyField,
          onExpandRow
        }) as ExtColumn<T>
    )
  )

  if (hasExpander) {
    const expanderColumn: ExtColumn<T> = {
      id: 'expander',
      expander: true,
      show: false
    }
    return [...parsedColumns, expanderColumn]
  }

  return parsedColumns
}

function hasExpanderColumn(columns) {
  return some(columns, c => c.expander)
}

function isShowActionOption<T>(
  option: ActionOption<T> = {} as ActionOption<T>,
  data: T[] = [],
  column: ColumnProps<T>
) {
  const { hide } = option
  const isFuncHide = hide && typeof hide === 'function'
  return some(data, x => (isFuncHide ? !hide(x, null, column) : !hide))
}

function isShowActionColumn<T>(
  actionOptions: ActionOption<T>[] = [],
  data: T[] = [],
  column: ColumnProps<T>
) {
  return some(actionOptions, item => isShowActionOption(item, data, column))
}

interface FactoryColumnByType<T> {
  columns: ColumnProps<T>[]
  resolvedState: () => any
  column: ColumnProps<T>
  index: number
  resourceTypes: ResourceTypeDict
  hideEmptyColumns: boolean
  data: T[]
  keyField: keyof T
  onExpandRow: (viewIndex: number, field: keyof T) => void
}
export const isDisabled = <T extends {}>(
  col: ColumnProps<T> | GridAction<T>,
  condition: (disabled: (row: T) => boolean) => boolean
) => {
  const { disabled } = col
  const isDisabledFn = disabled && typeof disabled === 'function'
  return isDisabledFn
    ? condition(disabled as (row: T) => boolean)
    : (disabled as boolean)
}

function getHeader<T>(
  column: ColumnProps<T, {}>,
  defaultColumnProps: ExtColumn<T>
) {
  const { descriptionColumn, headerTemplate } = column

  const defaultColumn = headerTemplate
    ? headerTemplate(column, defaultColumnProps)
    : defaultColumnProps.Header
  return descriptionColumn ? (
    <span className={styles.columnTooltip}>
      {defaultColumn}
      <span className={styles.iconTooltip}>
        <Tooltip title={descriptionColumn}>
          <i className={cx('material-icons')}>help</i>
        </Tooltip>
      </span>
    </span>
  ) : (
    defaultColumn
  )
}

export function factoryColumnByType<T>({
  column,
  columns,
  index,
  resolvedState,
  resourceTypes,
  hideEmptyColumns,
  data,
  keyField,
  onExpandRow
}: FactoryColumnByType<T>): ExtColumn<T> | void {
  const isEmptyData = data && !(data.length > 0)
  const fieldsColumn = keys(column)
  const hasHideProperty = includes(fieldsColumn, 'hide')
  let showColumn = !column.hide && true
  const hasSysField = includes(keys(ColumnInternalType), column.type)
  if (!isEmptyData && hideEmptyColumns && !hasSysField && !hasHideProperty) {
    showColumn = showColumn && some(data, item => !!item[column.field])
  }
  if (column.type === ColumnInternalType.actions) {
    const { actionOptions = [] } = column || ({} as ColumnProps<T>)
    showColumn = showColumn && isShowActionColumn(actionOptions, data, column)
  }
  const {
    type = ColumnExternalType.text,
    subColumns,
    style,
    headerStyle,
    onClick,
    clickable,
    descriptionCell,
    disabled,
    template
  } = column
  const hasSubColumns = subColumns && subColumns.length > 0

  const defaultColumnProps: ExtColumn<T> = {
    id: String(column.field),
    columnType: type,
    template,
    width: column.width,
    disabled,
    columns: hasSubColumns
      ? columnMapper({
          columns: subColumns,
          resourceTypes,
          resolvedState,
          hideEmptyColumns,
          data
        })
      : null,
    headerClassName: cx(
      getColumnTypeSelector(type),
      getColumnFieldSelector(String(column.field || ''))
    ),
    className:
      cx(styles.rowTable, column.className, column.field as string, type, {
        disabled: disabled === true
      }) || null,
    Header: column.title || '',
    accessor: String(column.field),
    show: showColumn,
    filterable: true,
    filterAll: true,
    style: { paddingLeft: '10px', ...style },
    headerStyle: {
      textAlign: hasSubColumns ? 'center' : 'left',
      padding: '15px 10px',
      ...headerStyle
    }
  }

  const defaultExternalTypeColumnProps: ExtColumn<T> = {
    ...defaultColumnProps,
    id: String(column.field) || uuidv4(),
    width: column.width,
    Header: getHeader(column, defaultColumnProps)
  }

  switch (type) {
    case ColumnInternalType.selector:
      if (!keyField)
        return {
          ...defaultColumnProps,
          isSelector: true,
          show: false,
          filterable: false
        }

      return {
        ...defaultColumnProps,
        isSelector: true
      }
    case ColumnInternalType.actions:
      return {
        ...defaultColumnProps,
        Header: column.title || 'Actions',
        sortable: false,
        filterable: false,
        minWidth: 75,
        style: {
          padding: 0,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        Cell: row => (
          <CellActions
            row={row}
            column={column}
            columns={columns}
            resolvedState={resolvedState}
          />
        )
      }
    case ColumnInternalType.severity:
      return {
        ...defaultColumnProps,
        accessor: String(column.field),
        sortable: false,
        width: 6,
        headerStyle: { padding: 0 },
        filterable: false,
        style: { padding: 0 },
        resizable: false,
        border: '0 none',
        Cell: row => <Severity row={row} column={column} />
      }

    case ColumnInternalType.button:
      return {
        ...defaultColumnProps,
        accessor: String(column.field) || 'button',
        sortable: false,
        headerStyle: { padding: 0 },
        width: 35,
        filterable: false,
        style: { padding: 0, cursor: 'pointer' },
        resizable: false,
        border: '0 none',
        Cell: row => {
          const canClick = clickable(row.original)
          function handleClick() {
            if (canClick) {
              onClick(row.original || ({} as T))
            }
          }
          return (
            <Tooltip title={descriptionCell}>
              <div role={'button_column_action'} onClick={handleClick}>
                {column.template ? column.template(row, column) : null}
              </div>
            </Tooltip>
          )
        }
      }

    case ColumnExtraType.iconGraph:
      if (!resourceTypes) {
        break
      }
      return {
        ...defaultColumnProps,
        accessor: String(column.field) || 'iconGraph',
        sortable: false,
        width: 35,
        headerStyle: { padding: 0 },
        filterable: false,
        style: {
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          flexDirection: 'column'
        },
        resizable: false,
        border: '0 none',
        Cell: row => {
          const defaultTemplate = (
            <IconGraph
              row={row}
              column={column}
              resourceTypes={resourceTypes}
            />
          )
          return column.template ? (
            <>{column.template(row, column, defaultTemplate)}</>
          ) : (
            defaultTemplate
          )
        }
      }
    case ColumnExternalType.number:
      return {
        ...defaultExternalTypeColumnProps,
        style: {
          textAlign: 'left',
          paddingLeft: 10
        },
        Cell: row => {
          const defaultTemplate = (
            <Cell
              isExpanded={(row || ({} as ExtCellInfo<T>)).isExpanded}
              viewIndex={(row || ({} as ExtCellInfo<T>)).viewIndex}
              data={(row || ({} as ExtCellInfo<T>)).original || ({} as T)}
              value={(row || ({} as ExtCellInfo<T>)).value}
              column={column as any}
              defaultValue={0}
              onExpandRow={onExpandRow}
            />
          )
          return column.template ? (
            <>{column.template(row, column, defaultTemplate)}</>
          ) : (
            defaultTemplate
          )
        }
      }
    case ColumnExternalType.datetime:
      return {
        ...defaultExternalTypeColumnProps,
        width: column.width || 215,
        Cell: row => {
          const rowNumValue = Number(row?.value || 0)
          const defaultTemplate = formatDateTime(
            isNaN(rowNumValue) ? row?.value || 0 : rowNumValue,
            column.format
          )
          return (
            <>
              {column.template
                ? column.template(row, column, defaultTemplate)
                : defaultTemplate}
            </>
          )
        }
      }

    case ColumnExternalType.boolean:
      return {
        ...defaultExternalTypeColumnProps,
        Cell: row => {
          const defaultTemplate = row.value ? 'Yes' : 'No'
          return (
            <>
              {column.template
                ? column.template(row, column, defaultTemplate)
                : defaultTemplate}
            </>
          )
        }
      }

    case ColumnExternalType.text:
    default:
      return {
        ...defaultExternalTypeColumnProps,
        Cell: row => {
          const defaultTemplate = (
            <Cell
              isExpanded={(row || ({} as ExtCellInfo<T>)).isExpanded}
              viewIndex={(row || ({} as ExtCellInfo<T>)).viewIndex}
              data={(row || ({} as ExtCellInfo<T>)).original || ({} as T)}
              value={(row || ({} as ExtCellInfo<T>)).value}
              column={column as any}
              onExpandRow={onExpandRow}
            />
          )
          return column.template ? (
            <>{column.template(row, column, defaultTemplate)}</>
          ) : (
            defaultTemplate
          )
        }
      }
  }
}
